import React, { useState, useEffect, useRef, useMemo } from "react";
import { useGlobalConfigs, useRsnFee } from "@directv-digital/dds.server.application-context";
import DtvPlanCardTopper from "src/components/video-lab/plan-cards/dtv-plan-card-topper";
import PkgComparisonModalV2Module, { IPackageComparisonModalV2Props } from "src/components/video-lab/plan-cards/pkg-comparison-modal-v2";
import { IExternalServiceComparisonChartProps } from "@directv-digital/dds.ui.dtv-external-service-comparison-chart";
import { PackageCardWrapper } from "src/components/video-lab/plan-cards/componetry";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { getCookie } from "@directv-digital/dds.services.cookie-service";
/** Styling */
import { EqualHeight, EqualHeightContext } from "react-equal-height/clean";
import { useTheme } from '@mui/material/styles';
import { useBreakPoints } from "@directv-digital/dds.utils";

/** Utility */
import {
  IPlanCardData, IPlanCardSets, defaultPackageVariant, planCardColorVariant,
  getCardsWithDynamicData,
  getChnlCountsFromCtaLabel,
  isValidPrice,
  hydrateRequiredPricePlaceholders
} from "src/components/video-lab/plan-cards/utils";
import { fireGa4ProductImpression } from "@directv-digital/dds.services.analytics-service";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { DtvPlanCard } from "src/components/video-lab/plan-cards//dtv-plan-card-v2";
import { TabsDataToggle } from "src/components/video-lab/plan-cards/dtv-toggle-plan-card";
import YotpoPackageRating from "src/components/video-lab/plan-cards/componetry/YotpoPackageRating/YotpoPackageRating";
import dynamic from "next/dynamic";
import { type ComparePkgTray, ComparePkgTrayProvider } from "./../../ComparePackageTray";

const ComparePackagesTray = dynamic(() => import("./../../ComparePackageTray"));

// Move this into directv-digital/dds.utils -> server utils
function safeRequestIdleCallback(callback, timeout?: number) {
  if (typeof requestIdleCallback === "function" || 'requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, timeout || 0);
  }
}

interface IDtvPlanCardModule {
  // -- Below >> From WnC Main Canvas --
  aboveTheFold? : boolean;
  // -- Below >> Server-generated --
  cards: IPlanCardSets; // derived from props.cards + props.cardsNoLocalChannels
  config: {
    showTopperZipcode?: boolean; // derived from props.isSearchZipCode
    isProgressivePkgCard?: boolean; // derived from props.packageCardVariant
    isProductToggleVariant?: boolean; // derived from component type
    isLocalsToggleOnlyVariant?: boolean; // derived from component type
    useGeminiFee?: boolean; // productType !== "byod"
  };
  // -- Below >> From CI --
  id: string;
  packageCardVariant?: string;
  mduVariant?: boolean;
  variant?: string;
  toggledSatellite?: boolean;
  isSatellite?: boolean;
  toggle?: any;
  satellitePageHeader?: any;
  isSearchZipCode?: boolean;
  zipCodeText?: any;
  promotions?: boolean;
  packageComparison?: IExternalServiceComparisonChartProps | IPackageComparisonModalV2Props;
  packageComparisonNoLocalChannels?: IExternalServiceComparisonChartProps | IPackageComparisonModalV2Props;
  packageChange?: any;
  selectedPackage?: string;
  setSelectedPackage?: any;
  open?: boolean;
  channelLogoStyles?: any;
  channelLogoContainerStyles?: any;
  channelLogoImageWrapperStyles?: any;
  orientation?: any;
  noTopBorder?: boolean;
  disableAutoTopper?: boolean;
  yotpoConfig?: {
    enableStarRating?: boolean;
    starColor?: string;
    analyticsLinkName?: string;
    productId?:  string;
    appKey?: string;
    scriptSrc?: string;
    className?: string;
    packageSubHeading?: string;
    packageHeading?: string;
    yotpoAttributes?:{
      className?: string;
      data_yotpo_instance_id?: string;
      data_yotpo_product_id?: string;
    }
  };
  productType?: string;
  embeddedSvods?: any;
  embeddedSvodSection?: boolean;
  comparePkgTray?: ComparePkgTray;
}

const getIsNoLocalsDefaultValue = (toggleData: TabsDataToggle) => {
  if (toggleData && toggleData?.toggles && toggleData?.enabled === true) {
    const selected = toggleData?.toggles?.filter(toggle => toggle?.selected === true)?.[0] || toggleData?.toggles?.[0];
    return selected?.value?.toLowerCase() === "no local channels"
  }
  return false;
}

export function DtvPlanCardV2Module(props: IDtvPlanCardModule) {
  try {
    const {
      // From WnC Main Canvas
      aboveTheFold,
      // Server-manipulated
      cards,
      config,
      // From CI Feed
      id,
      packageComparison,
      packageComparisonNoLocalChannels,
      toggledSatellite,
      isSatellite,
      zipCodeText,
      promotions,
      packageCardVariant = defaultPackageVariant,
      packageChange,
      selectedPackage,
      setSelectedPackage,
      mduVariant,
      toggle,
      // open,
      channelLogoStyles,
      channelLogoContainerStyles,
      channelLogoImageWrapperStyles,
      orientation,
      // productType, // only used on server side
      disableAutoTopper,
      satellitePageHeader,
      embeddedSvods,
      embeddedSvodSection
    } = props || null;
    const {
      showTopperZipcode = true,
      isProgressivePkgCard,
      isProductToggleVariant,
      isLocalsToggleOnlyVariant,
      useGeminiFee = true
    } = config;

    const globalConfigs = useGlobalConfigs();
    const { value: globalRsnFeeValue } = useRsnFee() ?? {};
    const { setForceUpdate: forceUpdate   } = React.useContext(EqualHeightContext);
    const pkgCompareModalRef = useRef<any>(null);
    // NOTE: the per-zip all-in recompute is NOT wired here on purpose. It lives on
    // the leaf that renders each price line (`LiveAllInText` → `useLiveAllIn`), so
    // a zip change re-renders that one `DtvText` instead of this whole module. It
    // replaces the withdrawn `data-dp-allin` DOM marker (WAT-11586), which fusion's
    // `DtvText` parser strips before it can reach the DOM.
    const theme = useTheme();
    const [onLoad, setOnLoad] = useState<boolean>(true);

    /**
     * -- Core State Values --
     * isCardSetSatellite - if the cards shown are satellite cards
     * displayNoLocals - control state of local/no-locals toggle, including locals eligibility
     * isLocalsEligible - if false, no locals toggle Z(if any) is hidden
     */
    const noLocalsToggleDfltVal =
      getIsNoLocalsDefaultValue(toggle);

    const isCardSetSatellite = useMemo(() => (isProductToggleVariant ? toggledSatellite : (isSatellite || false)), [toggledSatellite]);

    const [displayNoLocals, setDisplayNoLocals] =
      useState<boolean>(noLocalsToggleDfltVal);

    const [isLocalsEligible, setIsLocalsEligible] =
      useState<boolean | null>(false);

    /**
     * -- Dynamic Pricing States --
     * rsnFee - updating triggers reload of cardData
     * isPricesLoading - if true, show loading dots
     */
    const [isPricesLoading, setIsLoadingPrices] = useState<boolean>(true);
    const geminiFeeEnabled = useMemo(() => {
      if (isCardSetSatellite) {
        return globalConfigs?.planCardConfig?.[0]?.geminiFeeConfig?.[0]?.dtvs || false;
      }
      return globalConfigs?.planCardConfig?.[0]?.geminiFeeConfig?.[0]?.dtvi || false;
    }, [isCardSetSatellite]);

    /**
     * -- Card Set States --
     * cardData - All card sets to render. Initially only with injected
     *            static prices, set to dynamic data when rsnFee changes
     * activeCards - specific card set to display
     */
    const [cardData, setCardData] = useState<IPlanCardSets>(cards);

    const activeCards = useMemo<IPlanCardData[]>(() => {
      if (isProductToggleVariant) {
        if (toggledSatellite) {
          if (displayNoLocals) {
            return cardData?.satelliteNoLocals;
          }
          return cardData?.satelliteLocals;
        }
        return cardData?.locals;
      }
      if (displayNoLocals) {
        return cardData?.noLocals;
      }
      return cardData?.locals;
    }, [cardData, toggledSatellite, displayNoLocals]);
    const activeCardsLen = useMemo(() => activeCards?.length, [activeCards]);

    /**
     * @description Compute pricing data for ALL card sets
     * @param pRsnFee - rsn fee to use for computing prices
     * @param error - if true, use convert to "up to" rsn fee pricing
     */
    const setDynamicCardData = (
      { pRsnFee, error }:
      { pRsnFee: string, error: boolean }
    ) => {
      console.log('[PlanCardModule][setDynamicCardData][Start]', {
        incomingRsnFee: pRsnFee,
        error,
        isCardSetSatellite,
        displayNoLocals,
        isLocalsEligible,
        geminiFeeEnabled,
        useGeminiFee,
        cardSets: Object.keys(cards || {})
      });
      let newCardData = {} as IPlanCardSets;
      Object.keys(cards)?.forEach(key => {
        const rsnFeeNumParsed = parseFloat(pRsnFee);
        const { moddedCards } = getCardsWithDynamicData({
          cards: cards[key],
          rsnFeeNum: rsnFeeNumParsed,
          useGeminiFee,
          convertToUpToPricing: error || false
        });

        const hydratedCards = moddedCards?.map(card => hydrateRequiredPricePlaceholders(card, pRsnFee));

        newCardData = {
          ...newCardData,
          [key]: hydratedCards
        };
      });

      setCardData(() => newCardData);
      setIsLoadingPrices(false);
      setTimeout(() => forceUpdate?.(val => !val), 0);
    };

    /** --------------------------------------------------------------------------
     *  -- Callback function for Topper --
     * @description Because RSN Fee is Dynamic, setRsnFee triggers cardData reload
     * - Handle events related to rsnFeeChange
     * - if called with fee = "fetching", Triggers load dots via isLoadingPrices
     * - If called with fee = [value], triggers computing price copy update for all card sets */
    const handleRsnFeeChange = ({ fee, error }: { fee: string, error: boolean }) => {
      if (fee === "fetching") {
        setIsLoadingPrices(true);
        return;
      }

      if (error) {
        setDynamicCardData({
          pRsnFee: "19.99",
          error
        });
        // error path is fully handled with the fallback fee — without this
        // return, a valid `fee` recomputed the identical card data a 2nd time
        return;
      }

      if (!isValidPrice(fee)) {
        console.error(`[PlanCardModule] Tried updating cards with an invalid fee of '${fee}`);
        return;
      }

      setDynamicCardData({
        pRsnFee: error ? "19.99" : fee,
        error
      });
    }

    /** --------------------------------------------------------------------------
     *  -- Callback function for Topper --
     * - Sync state of locals toggle when clicked */
    const handleUseNoLocalsToggleChange = (value: boolean) => {
      setDisplayNoLocals(value);
    };

    /** --------------------------------------------------------------------------
     *  -- Callback function for Topper --
     *  - Update locals eligibility when zipcode changes
     */
    const handleUpdateLocalsEligible = (newEligibility: boolean) => {
      if ((isProductToggleVariant && toggledSatellite) || isLocalsToggleOnlyVariant || isCardSetSatellite) {
        // If was eligible but now ineligible, force switch to no locals
        // If was ineligible but now eligible, don't change UI state
        setDisplayNoLocals(prev => (!newEligibility ? true : !isLocalsEligible && newEligibility ? false : prev));
        setIsLocalsEligible(newEligibility);
      } else {
        setIsLocalsEligible(true);
      }
    }

    /** Equal Height */
    // Generated by Copilot- Start - 2026-03-27 14:53:32
    const { isMobile } = useBreakPoints();
    // Generated by Copilot- End - 2026-03-27 14:53:32
    const [disableEqualHeight, setDisableEqualHeight] = useState<boolean>(isProgressivePkgCard && isMobile);
    const [topperHeightArr, setTopperHeightArr] = useState<any>([]);
    const maxTopperHeight = useMemo(() => topperHeightArr?.reduce?.((accumulator, currentValue) => {
      if(!isNaN(currentValue)){
        return Math.max(accumulator, currentValue);
      }
      return accumulator;
    }, topperHeightArr?.[0]) || 0, [topperHeightArr]);

    /** --------------------------------------------------------------------------
     * Rendering starts-
     * --------------------------------------------------------------------------
     */
    useEffect(() => {
      // Generated by Copilot- Start - 2026-03-27 15:57:25
      // Note: resize handling for isMobile is managed by useBreakPoints internally.
      // disableEqualHeight is kept in sync via the dedicated useEffect([isMobile]) below.
      // Generated by Copilot- End - 2026-03-27 15:57:25

      handleUpdateLocalsEligible(
        Boolean((window.sessionStorage.getItem("isLocalsEligible") || "") !== "false")
      );
      const cookieFee = getCookie("rsnFee");
      if (isValidPrice(globalRsnFeeValue) || !showTopperZipcode) {
        setDynamicCardData({
          pRsnFee: globalRsnFeeValue,
          error: false
        });
      } else if (isValidPrice(cookieFee)) {
        setDynamicCardData({
          pRsnFee: cookieFee,
          error: false
        });
      }
      // else >> topper will fetch and pass fee thru handleRsnFeeChange
    }, []);

    useEffect(() => {
      // Generated by Copilot- Start - 2026-03-27 15:57:25
      // Always sync disableEqualHeight when isMobile or isProgressivePkgCard changes.
      // isProgressivePkgCard included to avoid stale closure if config/props update.
      setDisableEqualHeight(isProgressivePkgCard && isMobile);
      // Generated by Copilot- End - 2026-03-27 15:57:25
    }, [isMobile, isProgressivePkgCard]);

    /** When toggle-plan-card updates product toggle
     * - compute no locals default/value */
    useEffect(() => {
      if (onLoad) {
        setOnLoad(false);
        return;
      }
      if (isProductToggleVariant) {
        if (isCardSetSatellite && !isLocalsEligible) {
          // If showing satellite cards and user is ineligible,
          // force ensuring toggled to no locals
          setDisplayNoLocals(true);
          return;
        }
        setDisplayNoLocals(noLocalsToggleDfltVal);
      }
    }, [toggledSatellite]);

    /** --------------------------------------------------------------------------
     *  -- Analytics -- */
    const analyticsData = useMemo(() => activeCards?.map(
      (card, ix) => {
        const gaItemId = card?.googleAnalytics?.id || card?.productName?.content;
        return {
          ga: {
            id: gaItemId,
            position: ix
          },
          ga4Impressions: {
            item_id: gaItemId,
            item_name: gaItemId,
            index: ix
          }
        };
      }
    ), [activeCards]);

    // update analytics
    const hasFiredRef = useRef(false);

    useEffect(() => {
      if (!analyticsData || analyticsData.length === 0) {
        return;
      }

      if (hasFiredRef.current) {
        return;
      } // prevent second run in dev Strict Mode
      hasFiredRef.current = true;

      safeRequestIdleCallback(() => {
        fireGa4ProductImpression(analyticsData.map(ad => ad?.ga));
      }, 2000);
    }, [analyticsData]);

    /** --------------------------------------------------------------------------
     * -- Pkg Comparison Modal -- */
    const packagePrices = useMemo(() => {
      if(activeCards?.length) {
        return activeCards?.map(card => card?.productPrice?.price?.moddedContent || null);
      }
      return undefined
    }, [activeCards]);

    const openPkgComparisonModal = (sourceIx: number) => {
      if (pkgCompareModalRef?.current) {
        pkgCompareModalRef?.current?.updateOpenPkgCompareModal(sourceIx);
      }
    };

    const updateTopperHeightFn = (index, height: number) => {
      // functional update on a copy — the previous in-place mutation kept the
      // same array reference, so setTopperHeightArr never triggered a re-render
      setTopperHeightArr(prev => {
        const next = [...prev];
        next[index] = height;
        return next;
      });
    }
    const equalHeightKey = useMemo(
      () => `${disableEqualHeight ? 'eh-mobile' : 'eh-desktop'}-${activeCardsLen}-${isCardSetSatellite ? 'sat' : 'int'}`,
      [disableEqualHeight, activeCardsLen, isCardSetSatellite]
    );
    return (
      <ComparePkgTrayProvider
        compareText={props?.comparePkgTray?.compareText}
        isComparePkgTray={props?.comparePkgTray?.isCompareEnabled}
        maxSelectedTag={Number(props?.comparePkgTray?.maxSelectedPackages) || 3}
      >
      <>
        {props?.yotpoConfig?.enableStarRating && (
          <DtvBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "15px !important"
            }}
          >
            <YotpoPackageRating
              analyticsLinkName={props?.yotpoConfig?.analyticsLinkName}
              appKey={props?.yotpoConfig?.appKey}
              enableStarRating={props?.yotpoConfig?.enableStarRating}
              scriptSrc={props?.yotpoConfig?.scriptSrc}
              starColor={props?.yotpoConfig?.starColor}
              yotpoAttributes={[props?.yotpoConfig?.yotpoAttributes]}
            />
          </DtvBox>
        )}
        <DtvPlanCardTopper
          handleRsnFeeChange={handleRsnFeeChange}
          handleUpdateLocalsEligible={handleUpdateLocalsEligible}
          handleUseNoLocalsToggleChange={handleUseNoLocalsToggleChange}
          id={id}
          isCardSetSatellite={isCardSetSatellite}
          isLocalsEligible={isLocalsEligible}
          satellitePageHeader={satellitePageHeader}
          showNoLocalChannels={displayNoLocals}
          showTopperZipcode={showTopperZipcode}
          toggle={toggle}
          zipCodeText={zipCodeText}
        />
        <EqualHeight
        animationSpeed={0.2}
        key={equalHeightKey} timeout={0}>
          <PackageCardWrapper
            id={id} length={activeCardsLen}
            mduVariant={mduVariant}
            orientation={orientation}
            packageCardVariant={packageCardVariant}
          >
            {activeCards?.map((cardProps: IPlanCardData, cardIndex) => {
              const cardSpecificEmbeddedSvods = embeddedSvods?.filter(svod => svod?.packageListSystem?.includes(cardProps?.packageId)) || [];
              const bgArray =
                cardProps?.backgroundColor?.split(".");
              const bgColor =
                theme?.palette?.[bgArray?.[0]]?.[bgArray?.[1]] ||
                planCardColorVariant[cardProps?.variant]?.background ||
                cardProps?.backgroundColor;
              const barColorArray = cardProps?.barColor?.split(".");
              const highlightBarColor =
                theme?.palette?.[barColorArray?.[0]]?.[barColorArray?.[1]] ||
                planCardColorVariant[cardProps?.variant]?.bar || cardProps?.barColor;

              return (
                <DtvBox
                  id={`${id}-${cardIndex}-ctnr`}
                  key={cardIndex}
                  sx={{
                    width: `calc(100% / ${activeCardsLen} - ${activeCardsLen - 1} * 20px / ${activeCardsLen} )`,
                    maxWidth: {
                      xs: "370px",
                      sm: "300px"
                    },
                    backgroundColor: props?.noTopBorder ? "none" : `${bgColor}`,
                    height: props?.noTopBorder ? "fit-content" : "auto",
                    mr: {
                      xs: cardIndex === activeCardsLen - 1 ? 5 : 0,
                      sm: cardIndex === activeCardsLen - 1 ? 8 : 0,
                      md: 0,
lg: 0,
xl: 0
                    },
                    ...(isProgressivePkgCard && {
                      width: {
                        xs: "100%",
                        sm: mduVariant ? "100%" :
                          `calc(100% / ${activeCardsLen})`,
                        md: mduVariant ? "100%" :
                          `calc(100% / ${activeCardsLen} - ${activeCardsLen - 1} * 20px / ${activeCardsLen} )`
                      },
                      border: props?.noTopBorder ? `2px solid ${cardProps?.backgroundColor}` : "none",
                      borderRadius: props?.noTopBorder ? "8px" : "none",
                      borderBottom: props?.noTopBorder ? `10px solid ${cardProps?.backgroundColor}` : "none",
                      ...(promotions && {
                        marginTop: { sm: "26px" }
                      }),
                      ...(!cardProps?.topper && cardProps?.disableAutoTopper && {
                        marginTop: `${maxTopperHeight + 26}px`
                      } )
                    })
                }}>
                  <DtvPlanCard /** Intended if modified from server */
                    analytics={analyticsData?.[cardIndex]}
                    backgroundColor={cardProps?.backgroundColor}
                    cardFontColor={cardProps?.cardFontColor}
                        cardPosition={cardIndex}
                        disableAutoTopper={props?.disableAutoTopper}
                        googleAnalytics={cardProps?.googleAnalytics}
                    highlightBarColor={highlightBarColor}
                    id={`${id}-card-${cardIndex}`}
                    isMduVariant={mduVariant}
                    noTopBorder={props?.noTopBorder}
                        packageCardVariant={packageCardVariant}
                        queryparam={cardProps?.queryparam}
                    rewardTextColor={cardProps?.rewardTextColor}
                        variant={cardProps?.variant}
                        disableEqualHeight={disableEqualHeight}
                    /** Topper */
                    topper={promotions ? cardProps?.topper : null}
                        { ...( disableAutoTopper && { updateTopperHeightFn }) }

                    /** Top Section */
                    channelLogoContainerStyles={channelLogoContainerStyles}
                    channelLogoImageWrapperStyles={channelLogoImageWrapperStyles}
                    channelLogoStyles={channelLogoStyles}
                    channels={cardProps?.channels}
                        comparePackagesCta={cardProps?.comparePackagesCta}
                        cta={cardProps?.cta}
                        ctaPanel={cardProps?.ctaPanel}
                        lazyLoadImage={!aboveTheFold}

                    /** Pricing */
                    dpAllIn={cardProps?.pricingData?.dpAllIn}
                    embeddedSvodSection= {embeddedSvodSection}
                    featureHeading={cardProps?.featureHeading}
                        isLoadingPrices={isPricesLoading}

                    /** CTAs */
                    features={cardProps?.features}
                    open={props?.open}
                    optionalCTA={cardProps?.optionalCTA}
                    pkgUrl={cardProps?.pkgUrl}
                        packageChange={packageChange}

                    /** Bottom section */
                    productDesc={cardProps?.productDesc}
                        productName={cardProps?.productName}
                    productPrice={cardProps?.productPrice}
                        ribbon={cardProps?.ribbon}
                    seeAllChannelsCta={cardProps?.seeAllChannelsCta}
                    selectedPackage={selectedPackage}
                    whatYouGet={cardProps?.whatYouGet}
                        onComparePackagesClick={openPkgComparisonModal}
                        legalContent={cardProps?.legalContent}
                    /** Options */
                        showNoLocalChannels={displayNoLocals}
                        setSelectedPackage={setSelectedPackage}
                     /** Embedded Svods */
                        embeddedSvods={cardSpecificEmbeddedSvods}
                  />
                </DtvBox>
              );
            })}
          </PackageCardWrapper>
        </EqualHeight>
        {packageComparison && (
          <PkgComparisonModalV2Module
            chnlCountsByPackage={getChnlCountsFromCtaLabel(activeCards)}
            id={`${id}-pkg-comparison-modal`}
            isCardSetSatellite={isCardSetSatellite}
            modalFeed={packageComparison}
            modalFeedNoLocals={packageComparisonNoLocalChannels}
            packagePrices={packagePrices}
            ref={pkgCompareModalRef}
            toggledNoLocals={displayNoLocals}
          />
        )}
        <ComparePackagesTray  {...props?.comparePkgTray} originType="planCard" />
      </>
      </ComparePkgTrayProvider>
    );
  } catch (error) {
    console.error(`Error in Plan Card V2 Module >> ${error}`)
    return <ErrorDisplay component="DTV Plan Card Module" error={error} />;
  }
}
